import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { groupBy, toPairs, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  MemberType,
  ProjectId,
  RoleType,
  Team,
  TeamWithMeta,
} from 'api/models';
import { EventSource, ProjectAPI } from 'api/project';
import type { PlaceholderMappingObject } from 'api/project/placeholder-types';
import {
  AssignPlaceholderPayload,
  BulkAssignResponse,
  TeamReplacementProps,
  TeamsAPI,
} from 'api/teams';
import { Option } from 'components/molecules/squad-picker/types';
import { PlaceholderFormValues as FormValues } from 'components/organisms/placeholders/form';
import { GENERAL_API_STALE } from 'config/query-config';
import { MembersV2Response } from 'data/members/api';
import { queryClient as queryClientDefault } from 'queries';
import { getProjectMembersExclusionQueryKey } from 'queries/budget-members-keys';
import { logger } from 'services';
import {
  addProjectPlaceholder,
  removeProjectPlaceholder,
  updateProjectPlaceholder,
} from 'stores/memberships';
import { addTeam } from 'utils/idb-utils';
import { MEMBERS_V2_MODIFIED_QUERY_KEY, MEMBERS_V2_QUERY_KEY } from './members';

export interface CreatePlaceholderPayloadOpts {
  setCustomerRole?: boolean;
}

export interface MultiAssignPlaceholderPayload {
  option: Option;
  roleId?: number;
}

export type {
  PlaceholderMappingObject,
  PlaceholderMappingResponse,
} from 'api/project/placeholder-types';

export type PlaceholderResolution = Map<string, number[]>;

export const getCreatePlaceholderPayload = (
  { name, role, budget }: FormValues,
  options?: CreatePlaceholderPayloadOpts,
) => {
  const isCustomerRole =
    Number(role.value) === -1 || role.extra.type === RoleType.Customer;

  const budgetIds =
    budget && budget.length > 0
      ? budget
          .map((option) => option.value)
          .filter((id): id is number => id != null)
      : undefined;

  return {
    teamName: name,
    ...((options?.setCustomerRole || Number(role.value) !== -1) && {
      roleId: Number(role.value),
    }),
    teamType: isCustomerRole ? MemberType.External : MemberType.Native,
    ...(budgetIds &&
      budgetIds.length > 0 && { projectFinancialsBudgetIds: budgetIds }),
  };
};

export const createProjectPlaceholderMutationFn =
  (projectId: number, teamSource: EventSource) =>
  async (form: FormValues): Promise<Team> => {
    const team = await TeamsAPI.createProjectTeam({
      projectId,
      ...getCreatePlaceholderPayload(form),
      teamSource,
    });

    if (team) {
      const data =
        queryClientDefault.getQueryData<MembersV2Response>(
          MEMBERS_V2_QUERY_KEY,
        );

      team.projectIds = [projectId];

      if (data?.teams) {
        data.teams[team.teamId] = team;
      }

      queryClientDefault.setQueryData(MEMBERS_V2_QUERY_KEY, data);
      queryClientDefault.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
      queryClientDefault.invalidateQueries(
        getProjectMembersExclusionQueryKey(ProjectId(projectId)),
      );

      addTeam({
        ...team,
      }).catch((error) => {
        logger.error(
          `Error occured while updating placeholder insider indexedDB teamId: ${team?.teamId} projectId:${projectId}`,
          error,
        );
      });
    }

    return team;
  };

const createMutationFn = (formValues: FormValues) => {
  return TeamsAPI.createTeam(getCreatePlaceholderPayload(formValues));
};

const updateMutationFn = (
  id: number,
  { name, role }: Partial<FormValues>,
): Promise<Team> => {
  return TeamsAPI.updateTeam(id, {
    teamName: name,
    ...(role && { roleId: Number(role.value) }),
  });
};

export const useUpdatePlaceholderMutation = (
  id: number,
  options: UseMutationOptions<Team, unknown, Partial<FormValues>> = {},
) => useMutation((formValues) => updateMutationFn(id, formValues), options);

const singleAssignMutationFn = (
  projectId: number,
  teamId: number,
  userId: number,
  roleId?: number,
) => {
  /**
   * Teams PUT API is throwing 500, for single placeholder assignment,
   * hence using BULK API for the same, passing an array with single entry.
   */
  return TeamsAPI.bulkAssignPlaceholder([
    {
      projectId,
      teamId,
      roleId,
      userList: [
        {
          userId,
        },
      ],
    },
  ]);
};

const bulkAssignMutationFn = (
  projectId: number,
  placeholderOptions: MultiAssignPlaceholderPayload[],
) => {
  const payload: AssignPlaceholderPayload[] = [];

  placeholderOptions.forEach(({ option: _placeholder, roleId }) => {
    payload.push({
      projectId,
      userList:
        _placeholder.user?.extra?.type === 'user'
          ? [
              {
                userId: Number(_placeholder.user?.extra?.id),
              },
            ]
          : undefined,
      companyList:
        _placeholder.user?.extra?.type === 'company'
          ? [
              {
                companyId: Number(_placeholder.user?.extra.id),
              },
            ]
          : undefined,
      roleId,
      teamId: _placeholder.extra?.id,
    });
  });

  return TeamsAPI.bulkAssignPlaceholder(payload);
};

export const useMultiAssignPlaceholderMutation = (
  projectId: number,
  options: UseMutationOptions<
    BulkAssignResponse,
    unknown,
    MultiAssignPlaceholderPayload[]
  > = {},
) =>
  useMutation(
    (placeholderOptions) => bulkAssignMutationFn(projectId, placeholderOptions),
    options,
  );

export const useSingleAssignPlaceholderMutation = (
  projectId: number,
  teamId: number,
  userId: number,
  options: UseMutationOptions<
    BulkAssignResponse,
    unknown,
    number | undefined
  > = {},
) =>
  useMutation(
    (roleId?: number) =>
      singleAssignMutationFn(projectId, teamId, userId, roleId),
    options,
  );

export const useCreatePlaceholderMutation = (
  options: UseMutationOptions<Team, unknown, FormValues> = {},
) => useMutation(createMutationFn, options);

type ProjectPlaceholdersQueryKey = [string, number];
type ProjectPlaceholdersWithMetaQueryKey = [string, string, number];

export const getProjectPlaceholdersQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectPlaceholdersQueryKey => ['placeholders', projectId];

export const getPlaceholderResolutionByProjectQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectPlaceholdersQueryKey => [
  'placeholder_resolution',
  projectId,
];
export const getPlaceholderResolutionByProjectQueryKeyV2 = ([
  ,
  projectId,
]: ProjectId): ProjectPlaceholdersQueryKey => [
  'placeholder_resolution_v2',
  projectId,
];

export const getProjectPlaceholdersWithMetaQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectPlaceholdersWithMetaQueryKey => [
  'placeholders',
  'with-meta',
  projectId,
];

export const usePlaceholdersByProjectQuery = (
  projectId: ProjectId,
  queryOptions: UseQueryOptions<
    Team[],
    unknown,
    Team[],
    ProjectPlaceholdersQueryKey
  > = {},
): UseQueryResult<Team[]> =>
  useQuery(
    getProjectPlaceholdersQueryKey(projectId),
    () => TeamsAPI.getProjectPlaceholders(projectId),
    {
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );
export const usePlaceholdersWithMetaByProjectQuery = (
  projectId: ProjectId,
  queryOptions: UseQueryOptions<
    TeamWithMeta[],
    unknown,
    TeamWithMeta[],
    ProjectPlaceholdersWithMetaQueryKey
  > = {},
): UseQueryResult<TeamWithMeta[]> =>
  useQuery(
    getProjectPlaceholdersWithMetaQueryKey(projectId),
    () => TeamsAPI.getProjectPlaceholdersWithMeta(projectId),
    {
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );
const deleteMutationFn = (id: number) => TeamsAPI.deleteTeam(id);

export const useDeletePlaceholderMutation = (
  id: number,
  options: UseMutationOptions<Team, unknown> = {},
) => useMutation(() => deleteMutationFn(id), options);

export const useDeletePlaceholdersByRoleMutation = (
  options: UseMutationOptions<void, unknown, { roleId: number }> = {},
): UseMutationResult<void, unknown, { roleId: number }> =>
  useMutation<void, unknown, { roleId: number }>(
    ({ roleId }) => TeamsAPI.deleteTeamsByRole(roleId),
    options,
  );

type TeamsInAccountQueryKey = [string, string];

export const getTeamsInAccountQueryKey = (): TeamsInAccountQueryKey => [
  'teams',
  'all',
];

export const useAllTeamsInAccount = (
  queryOptions: UseQueryOptions<
    Team[],
    unknown,
    Team[],
    TeamsInAccountQueryKey
  > = {},
): UseQueryResult<Team[]> =>
  useQuery(getTeamsInAccountQueryKey(), () => TeamsAPI.getAllTeamsInAccount(), {
    staleTime: GENERAL_API_STALE,
    select: (teams) => uniqBy(teams, 'teamId'),
    ...queryOptions,
  });

export const useNativeTeamsInAccount = () => {
  const { data, ...rest } = useAllTeamsInAccount();
  const nativeTeams = useMemo(
    () =>
      data
        ?.filter((item) => item.teamType !== MemberType.External)
        .map(({ userList, companyList, ...restOfProps }) => restOfProps),
    [data],
  );

  return {
    data: nativeTeams,
    ...rest,
  };
};

export const useDefaultTeamsInAccount = () => {
  const { data, ...rest } = useAllTeamsInAccount();

  return {
    data: data?.filter((item) => !item.projectId),
    ...rest,
  };
};

export const useDefaultNativeTeamsInAccount = () => {
  const { data, ...rest } = useNativeTeamsInAccount();

  return {
    data: data?.filter((item) => !item.projectId),
    ...rest,
  };
};

export const useUpdateTeamNameMutation = (
  options: UseMutationOptions<Team, unknown, { id: number; name: string }> = {},
): UseMutationResult<Team, unknown, { id: number; name: string }> =>
  useMutation(({ id, name }) => updateMutationFn(id, { name }), options);

export const useAssignUserToTeamMutation = (
  options: UseMutationOptions<
    BulkAssignResponse,
    unknown,
    TeamReplacementProps
  > = {},
): UseMutationResult<BulkAssignResponse, unknown, TeamReplacementProps> =>
  useMutation(
    ({ projectId, teamId, userId, roleId }) =>
      singleAssignMutationFn(projectId, teamId, userId, roleId),
    options,
  );

export const useAddTeamToLocalState = (): ((
  projectId: number,
  team: Team,
) => void) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useCallback(
    (projectId: number, team: Team) => {
      queryClient.setQueryData<Team[]>(
        getTeamsInAccountQueryKey(),
        (oldValues = []) =>
          team.projectId != null
            ? [
                ...oldValues.filter(({ teamId }) => teamId !== team.teamId),
                team,
              ]
            : oldValues,
      );
      dispatch(
        addProjectPlaceholder({
          projectId: ProjectId(projectId),
          team,
        }),
      );
    },
    [dispatch, queryClient],
  );
};

export const useRemoveTeamFromLocalState = (): ((
  projectId: number,
  teamId: number,
) => void) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useCallback(
    (projectId: number, teamId: number) => {
      queryClient.setQueryData<Team[]>(
        getTeamsInAccountQueryKey(),
        (oldValues = []) =>
          oldValues.filter((team) =>
            team.projectId != null ? team.teamId !== teamId : true,
          ),
      );
      dispatch(
        removeProjectPlaceholder({
          projectId: ProjectId(projectId),
          teamId,
        }),
      );
    },
    [dispatch, queryClient],
  );
};
export const PlaceholderResolutionsQueryKey = ['placeholder_resolution'];

export const useChangeTeamResolvedStateInLocalState = (): ((
  projectId: number,
  teamId: number,
  resolved: boolean,
  userId?: number,
) => void) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useCallback(
    (projectId: number, teamId: number, resolved: boolean) => {
      const updatedTeams = queryClient.setQueryData<Team[]>(
        getTeamsInAccountQueryKey(),
        (oldValues = []) =>
          oldValues.map((team) =>
            team.projectId === projectId && team.teamId === teamId
              ? {
                  ...team,
                  resolved,
                }
              : team,
          ),
      );
      const updatedTeam = updatedTeams?.find(
        (team) => team.projectId === projectId && team.teamId === teamId,
      );

      if (updatedTeam != null) {
        dispatch(
          updateProjectPlaceholder({
            projectId: ProjectId(projectId),
            team: updatedTeam,
          }),
        );
      }
      queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
    },
    [dispatch, queryClient],
  );
};
export const useUnresolveTeamInLocalState = (): ((
  projectId: number,
  teamId: number,
) => void) => {
  const handler = useChangeTeamResolvedStateInLocalState();

  return useCallback(
    (projectId: number, teamId: number) => {
      handler(projectId, teamId, false);
    },
    [handler],
  );
};
export const useResolveTeamInLocalState = (): ((
  projectId: number,
  teamId: number,
  userId: number,
) => void) => {
  const handler = useChangeTeamResolvedStateInLocalState();

  return useCallback(
    (projectId: number, teamId: number, userId: number) => {
      handler(projectId, teamId, true, userId);
    },
    [handler],
  );
};

// deprecated
export const usePlaceholderResolutionByProjectQuery = (
  projectId: ProjectId,
  queryOptions: UseQueryOptions<
    PlaceholderResolution,
    unknown,
    PlaceholderResolution,
    ProjectPlaceholdersQueryKey
  > = {},
): UseQueryResult<PlaceholderResolution> =>
  useQuery(
    getPlaceholderResolutionByProjectQueryKey(projectId),
    async () => {
      const { mappings } = await ProjectAPI.getPlaceholderResolution(projectId);
      /**
       * placeholder can always be resolved to a single user
       * but the same user can be used to resolve multiple placeholders
       * hence to have a uniform type, array of ids is used
       * for both user and team mapping.
       */
      const userMap: { [id: string]: number[] } = {};

      /**
       * adding both userId and teamId as key and value
       * to facilitate easier lookup using both keys.
       */
      const teamMap = mappings.reduce((result, _mapping) => {
        const key =
          _mapping.user != null
            ? `user_${_mapping.user.userId}`
            : `company_${_mapping.company?.companyId}`;

        userMap[key] = [...(userMap[key] || []), _mapping.teamId];

        result[`team_${_mapping.teamId}`] =
          _mapping.user != null
            ? [_mapping.user.userId]
            : [_mapping.company?.companyId];

        return result;
      }, {} as PlaceholderResolution);

      return { ...userMap, ...teamMap };
    },
    {
      staleTime: GENERAL_API_STALE,
      refetchOnMount: true,
      ...queryOptions,
    },
  );
export interface ResolvedWithUser {
  identityType: 'USER';
  userId: number;
  budgetIds?: number[];
  roleId?: number;
}
export interface ResolvedWithCompany {
  identityType: 'COMPANY';
  companyId: number;
  budgetIds?: number[];
  roleId?: number;
}

export interface TeamInfoWithTaskIds {
  taskIds?: number[];
  roleId?: number;
  teamId: number;
}

export type PlaceholderMapping = Map<
  number,
  ResolvedWithUser | ResolvedWithCompany
>;
type PlaceholderResolvedWith = Map<string, number[]>;
export type PlaceholderResolvedWithTaskInfo = Map<
  string,
  TeamInfoWithTaskIds[]
>;
export interface PlaceholderResolutionV2 {
  resolvedWithEntities: PlaceholderResolvedWith;
  mappings: PlaceholderMapping;
  resolvedWithTaskIds: PlaceholderResolvedWithTaskInfo;
}
export const InitialPlaceholderResolutionV2: PlaceholderResolutionV2 = {
  resolvedWithEntities: {} as PlaceholderResolvedWith,
  mappings: {} as PlaceholderMapping,
  resolvedWithTaskIds: {} as PlaceholderResolvedWithTaskInfo,
};

const getPlaceholderResolutionMappingFromResponse = (
  mappings: PlaceholderMappingObject[],
): PlaceholderResolutionV2 => {
  return mappings.reduce((result, placeholderMapping) => {
    if (placeholderMapping.user != null) {
      const existingMappedPlaceholdersForUser =
        result.resolvedWithEntities[`user_${placeholderMapping.user.userId}`] ??
        [];

      const existingMappedPlaceholdersForUserWithTeamInfo: TeamInfoWithTaskIds[] =
        result.resolvedWithTaskIds[`user_${placeholderMapping.user.userId}`] ??
        [];

      return {
        resolvedWithEntities: {
          ...result.resolvedWithEntities,
          [`user_${placeholderMapping.user.userId}`]:
            existingMappedPlaceholdersForUser.concat(placeholderMapping.teamId),
        },
        mappings: {
          ...result.mappings,
          [placeholderMapping.teamId]: {
            identityType: 'USER',
            userId: placeholderMapping.user.userId,
            budgetIds: placeholderMapping.budgetIds,
            roleId: placeholderMapping.roleId,
          },
        },
        resolvedWithTaskIds: {
          ...result.resolvedWithTaskIds,
          [`user_${placeholderMapping.user.userId}`]:
            existingMappedPlaceholdersForUserWithTeamInfo.concat({
              teamId: placeholderMapping.teamId,
              taskIds: placeholderMapping.taskIds,
              roleId: placeholderMapping.roleId,
            }),
        },
      };
    }
    if (placeholderMapping.company != null) {
      const existingMappedPlaceholdersForUser =
        result.resolvedWithEntities[
          `company_${placeholderMapping.company.companyId}`
        ] ?? [];

      return {
        ...result,
        resolvedWithEntities: {
          ...result.resolvedWithEntities,
          [`company_${placeholderMapping.company.companyId}`]:
            existingMappedPlaceholdersForUser.concat(placeholderMapping.teamId),
        },
        mappings: {
          ...result.mappings,
          [placeholderMapping.teamId]: {
            identityType: 'COMPANY',
            companyId: placeholderMapping.company.companyId,
            budgetIds: placeholderMapping.budgetIds,
            roleId: placeholderMapping.roleId,
          },
        },
      };
    }

    return result;
  }, InitialPlaceholderResolutionV2);
};

export const usePlaceholderResolutionByProjectQueryV2 = (
  projectId: ProjectId,
  queryOptions: UseQueryOptions<
    PlaceholderResolutionV2,
    unknown,
    PlaceholderResolutionV2,
    ProjectPlaceholdersQueryKey
  > = {},
): UseQueryResult<PlaceholderResolutionV2> =>
  useQuery(
    getPlaceholderResolutionByProjectQueryKeyV2(projectId),
    async () => {
      const { mappings } = await ProjectAPI.getPlaceholderResolution(projectId);

      return getPlaceholderResolutionMappingFromResponse(mappings);
    },
    {
      staleTime: GENERAL_API_STALE,
      refetchOnMount: true,
      ...queryOptions,
    },
  );

export type PlaceholderResolutionsByProject = Map<
  number,
  PlaceholderResolutionV2
>;

export const usePlaceholderResolutionsQuery = (
  queryOptions: UseQueryOptions<
    PlaceholderResolutionsByProject,
    unknown,
    PlaceholderResolutionsByProject,
    typeof PlaceholderResolutionsQueryKey
  > = {},
): UseQueryResult<PlaceholderResolutionsByProject> =>
  useQuery(
    PlaceholderResolutionsQueryKey,
    async () => {
      const { mappings } =
        await ProjectAPI.getAllProjectsPlaceholderResolution();

      return toPairs(groupBy(mappings, 'projectId')).reduce(
        (result, [projectId, teamMappings]) => {
          result[projectId] =
            getPlaceholderResolutionMappingFromResponse(teamMappings);

          return result;
        },
        {} as PlaceholderResolutionsByProject,
      );
    },
    {
      staleTime: GENERAL_API_STALE,
      refetchOnMount: true,
      ...queryOptions,
    },
  );
